import {
  CreativeTagEvent,
  ViewabilityEnvironment,
  ViewabilityEventConditions
} from '../enums';
import { EventEmitter } from '../types';
import { ViewabilityObserver } from '../types/viewability-observer';

export class LoadedEventNotifier {
  private isEventEmitted = false;
  private isOnScreen: boolean;
  private isReady = true;
  private loadedEvent?: Event;

  constructor(
    observer: ViewabilityObserver,
    private readonly emitter: EventEmitter<CreativeTagEvent>,
    source?: EventEmitter<CreativeTagEvent>
  ) {
    // `Installed` marks the injection of the experience into the DOM, so no
    // viewability THRESHOLD gates it (ADV-32187) — a banner stuck at 95%
    // exposure is installed. But it must not fire on an SDK precache, and from
    // inside the webview the only signal separating a precache from a real
    // display is exposure: a precached ad stays at zero for its whole life.
    // Hence the single-pixel `OnScreen` gate, in InApp only — Ogury Web has no
    // precache.
    this.isOnScreen = observer.environment !== ViewabilityEnvironment.InApp;
    observer.on(ViewabilityEventConditions.OnScreen, () => {
      this.isOnScreen = true;
      this.tryEmit();
    });

    // With a `source` (rich path) the engine owns the readiness and its event
    // carries the `/ocs` payload, so this becomes an AND-gate. Without one
    // (static `image` path) readiness is implied by construction timing: the
    // player builds this after asset-load.
    if (source) {
      this.isReady = false;
      source.addEventListener(CreativeTagEvent.CreativeLoaded, evt => {
        this.isReady = true;
        this.loadedEvent = evt;
        this.tryEmit();
      });
    }

    this.tryEmit();
  }

  private tryEmit() {
    if (this.isEventEmitted || !this.isReady || !this.isOnScreen) {
      return;
    }
    this.isEventEmitted = true;
    if (this.loadedEvent !== undefined) {
      this.emitter.emit(CreativeTagEvent.CreativeLoaded, this.loadedEvent);
    } else {
      this.emitter.emit(CreativeTagEvent.CreativeLoaded);
    }
  }
}
